import flatten from 'lodash/flatten'
import React from 'react'

import { MosaicContext } from './contextTypes'
import { BoundingBox } from './lib/BoundingBox'
import { isParent } from './lib/mosaicUtilities'
import { MosaicBranch, MosaicDirection, MosaicKey, MosaicNode, ResizeOptions, TileRenderer } from './models/mosaic'
import { Split } from './Split'

export interface MosaicRootProps<T extends MosaicKey> {
  root: MosaicNode<T>;
  renderTile: TileRenderer<T>;
  resize?: ResizeOptions;
}

type MosaicRootState = {
  resizingSplit: { [path: string]: number };
}

export class MosaicRoot<T extends MosaicKey> extends React.PureComponent<MosaicRootProps<T>, MosaicRootState> {
  static contextType = MosaicContext

  declare context: MosaicContext<T>

  constructor(props: MosaicRootProps<T>) {
    super(props);
    this.state = { resizingSplit: {} };
  }

  render() {
    const { root } = this.props

    return <div className="mosaic-root">{this.renderRecursively(root, BoundingBox.empty(), [])}</div>
  }

  private renderRecursively(
    node: MosaicNode<T>,
    boundingBox: BoundingBox,
    path: MosaicBranch[],
  ): JSX.Element | JSX.Element[] {
    if (isParent(node)) {
      const pathKey = path.join(',');
      const splitPercentage = this.state.resizingSplit[pathKey] ?? node.splitPercentage ?? 50;
      const { first, second } = BoundingBox.split(boundingBox, splitPercentage, node.direction)

      return flatten(
        [
          this.renderRecursively(node.first, first, path.concat('first')),
          this.renderSplit(node.direction, boundingBox, splitPercentage, path),
          this.renderRecursively(node.second, second, path.concat('second')),
        ].filter(nonNullElement),
      )
    } else {
      let mosaicTileStyle: React.CSSProperties = BoundingBox.asStyles(boundingBox)
      const verticalLine = boundingBox.top + boundingBox.bottom === 100
      const horizontalLine = boundingBox.right + boundingBox.left === 100
    
      if (verticalLine || horizontalLine) {
        mosaicTileStyle = { ...mosaicTileStyle, opacity: '0' }
      }

      return (
        <div key={node} className="mosaic-tile" style={{ ...mosaicTileStyle }}>
          {this.props.renderTile(node, path)}
        </div>
      )
    }
  }

  private renderSplit(
    direction: MosaicDirection,
    boundingBox: BoundingBox,
    splitPercentage: number,
    path: MosaicBranch[],
  ) {
    const { resize } = this.props

    if (resize !== 'DISABLED') {
      return (
        <Split
          key={path.join(',') + 'splitter'}
          {...resize}
          boundingBox={boundingBox}
          splitPercentage={splitPercentage}
          direction={direction}
          onChange={(percentage) => this.onResize(percentage, path, true)}
          onRelease={(percentage) => this.onResize(percentage, path, false)}
        />
      )
    } else {
      return null
    }
  }

  private onResize = (percentage: number, path: MosaicBranch[], suppressOnRelease: boolean) => {
    const pathKey = path.join(',');
    const resizingUpdates = { ...this.state.resizingSplit, [pathKey]: percentage };

    if (suppressOnRelease) {
      // If we are dragging, only update the internal state until mouse released
      this.setState({ resizingSplit: resizingUpdates });
    } else {
      this.context.mosaicActions.updateTree(
        [
          {
            path,
            spec: {
              splitPercentage: {
                $set: percentage,
              },
            },
          },
        ],
        suppressOnRelease,
      )

      // Remove the temporary resizing state
      const newResizingSplit = { ...this.state.resizingSplit };
      delete newResizingSplit[pathKey];
      this.setState({ resizingSplit: newResizingSplit });
    }

  }
}

function nonNullElement(x: JSX.Element | JSX.Element[] | null): x is JSX.Element | JSX.Element[] {
  return x !== null
}
